import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Timer } from 'lucide-react';
import { riskApi, type ActiveCooldownItem } from '../../api/endpoints';
import { ApiError } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';

function formatCooldownReason(reason: string): string {
  const map: Record<string, string> = {
    loss: 'After loss',
    stopout: 'After stopout',
    volatility_shock: 'Volatility shock',
  };
  if (map[reason]) return map[reason];
  return reason.replace(/_/g, ' ');
}

function formatRemaining(remainingMs: number): string {
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return '0:00';
  const totalSec = Math.max(0, Math.ceil(remainingMs / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

function remainingMsForItem(item: ActiveCooldownItem, now: number): number {
  const t = Date.parse(item.expiresAt);
  if (!Number.isFinite(t)) return 0;
  return t - now;
}

function cooldownFetchErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.status === 404
      ? 'Cooldown API missing (deploy server with GET /api/risk/cooldowns?)'
      : `${error.message}${error.status ? ` (${error.status})` : ''}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Could not load cooldown status';
}

export function CooldownHeaderIndicator() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const queryEnabled = isAuthenticated && !authLoading;

  const { data, error, isPending, isError, isSuccess } = useQuery({
    queryKey: ['risk', 'cooldowns'],
    queryFn: riskApi.getActiveCooldowns,
    enabled: queryEnabled,
    refetchInterval: 20_000,
    staleTime: 10_000,
  });

  const sorted = useMemo(() => {
    const list = data?.items;
    if (!list?.length) return [];
    return [...list].sort((a, b) => Date.parse(a.expiresAt) - Date.parse(b.expiresAt));
  }, [data?.items]);

  if (authLoading || !isAuthenticated) {
    return null;
  }

  if (isPending) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
          'border-muted-foreground/25 bg-muted/50 text-muted-foreground animate-pulse'
        )}
        role="status"
        aria-busy="true"
        aria-label="Loading cooldown status"
      >
        <Timer className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
        <span className="hidden sm:inline">Cooldown…</span>
      </div>
    );
  }

  if (isError) {
    const msg = cooldownFetchErrorMessage(error);
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="alert"
            className={cn(
              'flex max-w-[min(100vw-8rem,280px)] cursor-default items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
              'border-destructive/55 bg-destructive/10 text-destructive dark:text-red-300'
            )}
            aria-label={`Cooldown status unavailable: ${msg}`}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="hidden sm:inline">Cooldown</span>
            <span className="truncate">error</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-xs text-left text-xs">
          <p className="font-semibold">Could not load cooldowns</p>
          <p className="mt-1 text-muted-foreground">{msg}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (!isSuccess) {
    return null;
  }

  if (sorted.length === 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="status"
            className={cn(
              'flex max-w-[min(100vw-8rem,280px)] cursor-default items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
              'border-muted-foreground/35 bg-muted/40 text-muted-foreground'
            )}
            aria-label="No symbol cooldown active. New cooldowns appear here after loss, stopout, or volatility shock."
          >
            <Timer className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
            <span className="hidden sm:inline">Cooldown</span>
            <span className="font-mono tabular-nums">—</span>
            <span className="hidden text-[0.7rem] opacity-90 md:inline">clear</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-xs text-left text-xs">
          <p className="font-semibold">No symbol cooldown</p>
          <p className="mt-1 text-muted-foreground">
            Nothing is blocking new entries on cooldown right now. After a loss or stopout, a timed cooldown can appear
            here with a live countdown.
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  const next = sorted[0];
  const nextRemaining = next ? remainingMsForItem(next, now) : 0;

  const distinctSymbols = new Set(sorted.map((i) => i.symbol)).size;
  const summarySymbol =
    sorted.length === 1 && next ? next.symbol : distinctSymbols === 1 && next ? next.symbol : null;

  const tooltipLines = sorted.map((item) => {
    const rem = remainingMsForItem(item, now);
    return `${item.symbol} — ${formatCooldownReason(item.reason)} — ${formatRemaining(rem)}`;
  });

  const shortLabel =
    summarySymbol != null
      ? summarySymbol
      : `${distinctSymbols} symbol${distinctSymbols === 1 ? '' : 's'}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          role="status"
          className={cn(
            'flex max-w-[min(100vw-8rem,280px)] cursor-default items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
            'border-orange-500/55 bg-orange-500/15 text-orange-900 dark:border-orange-400/45 dark:bg-orange-500/10 dark:text-orange-200',
            'shadow-sm transition-colors'
          )}
          aria-label={`Symbol cooldown active. Next clears in ${formatRemaining(nextRemaining)}.`}
        >
          <Timer className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
          <span className="hidden shrink-0 sm:inline">Cooldown</span>
          <span
            className="min-w-0 truncate font-mono tabular-nums tracking-tight"
            aria-live="polite"
            aria-atomic="true"
          >
            {formatRemaining(nextRemaining)}
          </span>
          <span className="truncate text-[0.7rem] font-medium opacity-90">· {shortLabel}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end" className="max-w-xs text-left text-xs">
        <p className="mb-1.5 text-sm font-semibold">Active cooldowns</p>
        <div className="space-y-1 font-mono">
          {sorted.map((item, i) => (
            <div key={item.id}>{tooltipLines[i]}</div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
